import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import {
  Avatar,
  Checkbox,
  type InputProps,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  debounce,
  useTheme,
} from '@mui/material';
import { Image } from '@realadvisor/image';
import { HouseIcon } from 'lucide-react';

import type {
  LotSelectFragment,
  Lots_Bool_Exp,
  SearchLotsQuery,
} from '@graphql/__generated__/graphql';
import {
  RaAutoComplete,
  type RaAutocompleteProps,
} from '@modules/_shared/components/data-grid/RaAutoComplete';
import { SelectStartIcon } from '@modules/_shared/components/form/SelectStartIcon';
import { formatAddress } from '@modules/_shared/utils/formatting';
import { useLocale } from '@shared/hooks/locale';
import { customPalette } from '@shared/theme/StyleProvider';

const LOT_SELECT_FRAGMENT = gql`
  fragment LotSelect on lots {
    id
    title
    property {
      id
      route
      street_number
      postcode
      locality
      country_code
      property_images(order_by: { order_nr: asc }, limit: 1) {
        order_nr
        image {
          id
          url
        }
      }
    }
    broker {
      id
      full_name
    }
  }
`;

export const SEARCH_LOTS = gql`
  ${LOT_SELECT_FRAGMENT}
  query SearchLots(
    $q: String!
    $where: lots_bool_exp
    $limitToDevelopment: uuid
  ) {
    search_lots_tsv(
      args: { search_text: $q, dev_id: $limitToDevelopment }
      limit: 10
      order_by: { created_at: desc }
      where: $where
    ) {
      id
      ...LotSelect
    }
  }
`;

type GetDefaultLotQuery = {
  lots_by_pk: LotSelectFragment | null;
};

const GET_DEFAULT_LOT = gql`
  ${LOT_SELECT_FRAGMENT}
  query GetDefaultLot($id: uuid!) {
    lots_by_pk(id: $id) {
      id
      ...LotSelect
    }
  }
`;

interface LotSelectProps
  extends Pick<
    RaAutocompleteProps<LotSelectFragment, false>,
    'autoFocus' | 'disabled' | 'sx' | 'InputProps' | 'loading'
  > {
  lotId?: string;
  onChange?: (lotId: string | null) => void;
  InputProps?: InputProps;
  filters?: Lots_Bool_Exp;
  limitToDevelopment?: string;
}

export const LotSelectItem: React.FC<{
  lot: LotSelectFragment;
  props: React.HTMLAttributes<HTMLLIElement>;
  selected?: boolean;
}> = ({ lot, props, selected }) => {
  const { text, palette: colors } = useTheme();

  return (
    <ListItem
      {...props}
      key={lot.id}
      style={{
        padding: '1px 10px',
      }}
    >
      <ListItemIcon>
        {selected != null && (
          <Checkbox
            edge="start"
            checked={selected}
            tabIndex={-1}
            disableRipple
          />
        )}
        <ListItemAvatar sx={{ paddingRight: 1 }}>
          <Avatar variant="rounded" sx={{ width: 55, height: 55 }}>
            {lot?.property?.property_images &&
            lot?.property?.property_images.length > 0 ? (
              <Image
                src={lot?.property?.property_images[0]?.image.url ?? ''}
                options={{ w: 256, f: 'jpg' }}
                objectFit="cover"
              />
            ) : (
              <HouseIcon
                style={{ margin: 8 }}
                size={32}
                color={customPalette.gray300}
              />
            )}
          </Avatar>
        </ListItemAvatar>
      </ListItemIcon>
      <ListItemText
        primary={
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, ...text.ellipsis }}>{lot?.title}</div>
            {lot != null && (
              <div
                style={{
                  ...text.caption,
                  ...text.ellipsis,
                  color: colors.grey[600],
                }}
              >
                {formatAddress(lot?.property, ', ')}
              </div>
            )}
          </div>
        }
        secondary={lot?.broker?.full_name}
      />
    </ListItem>
  );
};

export const LotSelect = ({
  onChange,
  lotId,
  autoFocus = false,
  disabled = false,
  loading: externalLoading = false,
  sx,
  InputProps,
  filters,
  limitToDevelopment,
}: LotSelectProps) => {
  const { t } = useLocale();
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = useState<LotSelectFragment[]>([]);
  const prevLotId = React.useRef<string | undefined>(lotId);
  const [selectedValue, setSelectedValue] = useState<
    (LotSelectFragment & { isRestricted?: true }) | null
  >(null);

  const { data: defaultLot } = useQuery<GetDefaultLotQuery>(GET_DEFAULT_LOT, {
    skip: !lotId,
    variables: { id: lotId },
  });

  useEffect(() => {
    if (defaultLot?.lots_by_pk != null) {
      setSelectedValue(defaultLot.lots_by_pk);
    } else if (lotId != null) {
      // We have a listing but we don't have access to it (permission denied).
      setSelectedValue({
        id: lotId,
        property: {
          id: lotId,
          property_images: [],
        },
        broker: null,
        isRestricted: true,
      });
    }
  }, [defaultLot, lotId, t]);

  if (prevLotId.current !== lotId) {
    prevLotId.current = lotId;

    if (lotId == null) {
      setSelectedValue(null);
    }
  }

  const [searchLots, { loading: searchLoading, error }] =
    useLazyQuery<SearchLotsQuery>(SEARCH_LOTS, {
      notifyOnNetworkStatusChange: true,
    });

  const debouncedSearch = useMemo(
    () =>
      debounce(value => {
        searchLots({
          variables: {
            q: value,
            where: filters,
            limitToDevelopment,
          },
        }).then(response => {
          setOpen(true);
          setOptions(response.data?.search_lots_tsv ?? []);
        });
      }, 275),
    [searchLots, filters, limitToDevelopment],
  );

  return (
    <RaAutoComplete<LotSelectFragment & { isRestricted?: true }>
      sx={sx}
      InputProps={{
        ...InputProps,
        startAdornment: InputProps?.startAdornment ?? (
          <SelectStartIcon icon={HouseIcon} />
        ),
        placeholder: InputProps?.placeholder ?? t('Search listings'),
      }}
      open={open}
      setOpen={setOpen}
      options={options}
      setOptions={setOptions}
      loading={externalLoading || searchLoading}
      value={selectedValue}
      error={error != null}
      autoFocus={autoFocus}
      debouncedSearch={debouncedSearch}
      onChange={(_event, value) => {
        setSelectedValue(value);
        onChange?.(value?.id ?? null);
      }}
      disabled={disabled || selectedValue?.isRestricted}
      getOptionLabel={option =>
        option.isRestricted
          ? t('Restricted listing')
          : formatAddress(option.property, ', ')
      }
      tooltip={
        selectedValue?.isRestricted
          ? t("You don't have access to this listing")
          : undefined
      }
      renderOption={(props, option) => (
        <LotSelectItem lot={option} props={props} />
      )}
    />
  );
};
